import {
  Component,
  OnInit,
  inject,
  output,
  signal,
  computed,
  effect,
  viewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { debounceTime } from 'rxjs/operators';
import { MatTree, MatTreeModule } from '@angular/material/tree';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatRadioModule } from '@angular/material/radio';
import { RootcategoriesService } from '../rootcategories/rootcategories.service';

export interface CategoriesNode {
  id: number;
  nombre: string;
  children?: CategoriesNode[];
}

@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.css'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatTreeModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatRadioModule,
  ],
})
export class SelectComponent implements OnInit {
  private readonly rootcategoriesService = inject(RootcategoriesService);

  readonly idSelected = output<number>();

  readonly categories = signal<CategoriesNode[]>([]);
  readonly loading = signal(true);

  readonly searchControl = new FormControl('');

  private readonly searchQuery = toSignal(this.searchControl.valueChanges.pipe(debounceTime(200)), {
    initialValue: '',
  });

  readonly filteredTree = computed(() => {
    const query = (this.searchQuery() ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
    if (!query) return this.categories();
    return this.filterNodes(this.categories(), query);
  });

  private readonly treeRef = viewChild<MatTree<CategoriesNode>>('tree');

  constructor() {
    effect(() => {
      const query = (this.searchQuery() ?? '').trim();
      const tree = this.treeRef();
      if (tree && query) {
        queueMicrotask(() => tree.expandAll());
      }
    });
  }

  ngOnInit(): void {
    this.rootcategoriesService.listCategories().subscribe({
      next: ({ body }) => {
        this.categories.set(body);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.rootcategoriesService.openSnacBar('Cant load categories list');
      },
    });
  }

  childrenAccessor = (node: CategoriesNode) => node.children ?? [];

  hasChild = (_: number, node: CategoriesNode) => !!node.children && node.children.length > 0;

  handleCheck(id: number): void {
    this.idSelected.emit(id);
  }

  clearSearch(): void {
    this.searchControl.setValue('');
  }

  private filterNodes(nodes: CategoriesNode[], query: string): CategoriesNode[] {
    const result: CategoriesNode[] = [];
    for (const node of nodes) {
      const filteredChildren = node.children ? this.filterNodes(node.children, query) : [];
      const nameNorm = node.nombre
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
      if (nameNorm.includes(query) || filteredChildren.length > 0) {
        result.push({ ...node, children: filteredChildren });
      }
    }
    return result;
  }
}
